// AdminLeagueDetail.js
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../supabase/supabase";

const th = {
  padding: "8px",
  borderBottom: "1px solid #ccc",
  textAlign: "left"
};

const td = {
  padding: "8px",
  borderBottom: "1px solid #eee",
  verticalAlign: "top",
  fontFamily: "monospace"
};

function AdminLeagueDetail() {
  const { leagueId } = useParams();
  const [league, setLeague] = useState(null);
  const [members, setMembers] = useState([]);
  const [draftMeta, setDraftMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [currentWeek, setCurrentWeek] = useState("Preseason");
  const [newWeekValue, setNewWeekValue] = useState("");

  useEffect(() => {
    const fetchLeagueAndMembers = async () => {
      try {
        const { data: leagueData } = await supabase.from("leagues").select("*").eq("id", leagueId).single();
        if (!leagueData) {
          setLeague(null);
          return;
        }

        setLeague(leagueData);

        const { data: membersData } = await supabase.from("league_members").select("*").eq("league_id", leagueId);
        const membersList = (membersData || []).map(m => ({
          id: m.user_id,
          ...m
        }));
        setMembers(membersList);

        const { data: draftData } = await supabase.from("drafts").select("*").eq("league_id", leagueId).single();
        if (draftData) {
          setDraftMeta(draftData);
        } else {
          setDraftMeta(null);
        }

        // Fetch current week from global config
        const { data: configData } = await supabase.from("config").select("*").eq("key", "season").single();
        if (configData) {
          const globalCurrentWeek = configData.value?.currentWeek || "Preseason";
          setCurrentWeek(globalCurrentWeek);
          setNewWeekValue(globalCurrentWeek);
        }

      } catch (err) {
        console.error("Error fetching league detail:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeagueAndMembers();
  }, [leagueId, refreshFlag]);

  const refresh = () => setRefreshFlag(f => !f);

  const handleUpdateCurrentWeek = async () => {
    if (!newWeekValue.trim()) {
      alert("Please enter a week value.");
      return;
    }

    if (!window.confirm(`Update global current week to "${newWeekValue}"? This will affect ALL leagues.`)) {
      return;
    }

    try {
      // Fetch existing config value first to merge
      const { data: configData } = await supabase.from("config").select("*").eq("key", "season").single();
      const existingValue = configData?.value || {};
      await supabase.from("config").update({
        value: { ...existingValue, currentWeek: newWeekValue.trim(), lastUpdated: new Date().toISOString() }
      }).eq("key", "season");

      setCurrentWeek(newWeekValue.trim());
      alert(`✅ Current week updated to "${newWeekValue.trim()}"`);
    } catch (err) {
      console.error("Error updating current week:", err);
      alert("Failed to update current week: " + err.message);
    }
  };

  const weekOptions = [
    "Preseason",
    "Week 1", "Week 2", "Week 3", "Week 4", "Week 5", "Week 6",
    "Week 7", "Week 8", "Week 9", "Week 10", "Week 11", "Week 12",
    "Week 13", "Week 14", "Week 15", "Week 16", "Week 17",
    "Conference Championships",
    "CFP Playoffs",
    "National Championship",
    "Offseason"
  ];

  const handleKick = async (userId) => {
    if (!window.confirm("Kick this user from the league? This cannot be undone.")) return;
    try {
      await supabase.from("league_members").delete().eq("league_id", leagueId).eq("user_id", userId);
      refresh();
    } catch (err) {
      console.error("Error kicking user:", err);
    }
  };

  const handlePromote = async (userId) => {
    if (!window.confirm("Promote this user to league admin?")) return;
    try {
      await supabase.from("leagues").update({ admin_id: userId }).eq("id", leagueId);
      refresh();
    } catch (err) {
      console.error("Error promoting user:", err);
    }
  };

  const handleResetDraft = async () => {
    if (!window.confirm("This will delete all draft data and clear every lineup. Continue?")) return;

    try {
      await supabase.from("drafts").delete().eq("league_id", leagueId);

      await supabase.from("leagues").update({ draft_complete: false }).eq("id", leagueId);

      const { data: membersData } = await supabase.from("league_members").select("user_id").eq("league_id", leagueId);
      const clears = (membersData || []).map(m =>
        supabase.from("league_members").update({
          points: 0,
        }).eq("league_id", leagueId).eq("user_id", m.user_id)
      );

      await Promise.all(clears);

      alert("✅ Draft reset successfully.");
      refresh();
    } catch (err) {
      console.error("Error resetting draft:", err);
      alert("Failed to reset draft: " + err.message);
    }
  };

  const handleSeedRemainingUsers = async () => {
    if (!league) return;

    const needed = league.max_managers - members.length;
    if (needed <= 0) {
      alert("League is already full.");
      return;
    }

    try {
      const { data: usersData } = await supabase.from("users").select("*");
      const currentIds = new Set(members.map(m => m.id));
      const available = (usersData || []).filter(u => !currentIds.has(u.id));

      if (available.length < needed) {
        alert("Not enough users to fill the league.");
        return;
      }

      const selected = available.sort(() => 0.5 - Math.random()).slice(0, needed);

      const batchAdds = selected.map(async (userData) => {
        const uidSuffix = userData.id.slice(-4);

        const teamName =
          userData.team_name?.trim() ||
          `Team ${uidSuffix}`;

        await supabase.from("league_members").insert({
          league_id: leagueId,
          user_id: userData.id,
          team_name: teamName,
          points: 0,
        });
      });

      await Promise.all(batchAdds);
      alert(`Added ${needed} user(s) to the league.`);
      refresh();

    } catch (err) {
      console.error("Error seeding users:", err);
      alert("Failed to add users: " + err.message);
    }
  };

  const draftStatus = () => {
    if (!draftMeta) return <span style={{ color: "gray" }}>Not started</span>;
    if (draftMeta.draft_complete) return <span style={{ color: "green" }}>✅ Complete</span>;
    return <span style={{ color: "orange" }}>🕐 In Progress</span>;
  };

  const formatDraftOrderType = () => {
    if (league.draft_order_type === "random") {
      return "Random Order";
    } else if (league.draft_order_type === "admin") {
      return "Commissioner Sets Order";
    }
    return league.draft_order_type || "Not Set";
  };

  if (loading) return <p>Loading league data...</p>;
  if (!league) return <p>League not found.</p>;

  return (
    <div style={{
      minHeight: "100vh",
      backgroundColor: "#ffffff",
      padding: "0"
    }}>
      <div style={{
        padding: "2rem",
        backgroundColor: "#ffffff",
        minHeight: "100vh"
      }}>
        <h2>Admin View: {league.name}</h2>

        {/* Current Week Management Section */}
        <div style={{
          backgroundColor: "#f0f8ff",
          border: "1px solid #0ea5e9",
          borderRadius: "8px",
          padding: "1rem",
          marginBottom: "2rem"
        }}>
          <h3 style={{ margin: "0 0 1rem 0", color: "#0c4a6e" }}>Season Management</h3>
          <p><strong>Current Week (Global):</strong> <span style={{ color: "#1e40af", fontSize: "1.1em" }}>{currentWeek}</span></p>

          <div style={{ marginTop: "1rem", display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
            <select
              value={newWeekValue}
              onChange={(e) => setNewWeekValue(e.target.value)}
              style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid #ccc" }}
            >
              {weekOptions.map(week => (
                <option key={week} value={week}>{week}</option>
              ))}
            </select>

            <button
              onClick={handleUpdateCurrentWeek}
              style={{
                backgroundColor: "#1e40af",
                color: "white",
                border: "none",
                padding: "0.5rem 1rem",
                borderRadius: "4px",
                cursor: "pointer"
              }}
            >
              Update Global Week
            </button>
          </div>

          <p style={{ fontSize: "0.9em", color: "#64748b", marginTop: "0.5rem", marginBottom: 0 }}>
            ⚠️ This updates the current week for ALL leagues globally.
          </p>
        </div>

        {/* League Info */}
        <p><strong>League ID:</strong> {league.id}</p>
        <p><strong>Admin UID:</strong> {league.admin_id}</p>
        <p><strong>Created By:</strong> {league.created_by}</p>
        <p><strong>Scoring Type:</strong> {league.scoring_type}</p>
        <p><strong>Max Managers:</strong> {league.max_managers}</p>
        <p><strong>Members:</strong> {members.length}</p>
        <p><strong>Draft Status:</strong> {draftStatus()}</p>
        <p><strong>Draft Order:</strong> {formatDraftOrderType()}</p>
        <p><strong>League Current Week:</strong> {league.current_week || "Not Set"}</p>

        <div style={{ marginTop: "1rem" }}>
          <button onClick={handleSeedRemainingUsers}>
            Seed Remaining Users
          </button>

          <button onClick={handleResetDraft} style={{ marginLeft: "1rem", color: "red" }}>
            🗑️ Reset Draft
          </button>
        </div>

        <h3 style={{ marginTop: "2rem" }}>League Members</h3>
        {members.length === 0 ? (
          <p>No members in this league.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "1rem", fontSize: "0.9rem" }}>
            <thead>
              <tr style={{ backgroundColor: "#f5f5f5" }}>
                <th style={th}>UID</th>
                <th style={th}>Display Name</th>
                <th style={th}>Team Name</th>
                <th style={th}>Email</th>
                <th style={th}>Points</th>
                <th style={th}>Trip Play</th>
                <th style={th}>Freezes</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m, idx) => (
                <tr key={m.id} style={{ backgroundColor: idx % 2 === 0 ? "#fafafa" : "white" }}>
                  <td style={td}>{m.id}</td>
                  <td style={td}>{m.displayName}</td>
                  <td style={td}>{m.team_name}</td>
                  <td style={td}>{m.email}</td>
                  <td style={td}>{m.points || 0} pts</td>
                  <td style={td}>{m.has_trip_play ? "Available" : "Used"}</td>
                  <td style={td}>{m.freezes_remaining ?? 0}</td>
                  <td style={td}>
                    {m.id !== league.admin_id ? (
                      <>
                        <button onClick={() => handleKick(m.id)} style={{ marginRight: "0.5rem", color: "red" }}>
                          Kick
                        </button>
                        <button onClick={() => handlePromote(m.id)}>
                          Promote
                        </button>
                      </>
                    ) : (
                      <em>Admin</em>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AdminLeagueDetail;
